import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XIcon, PlusIcon, TrashIcon } from 'lucide-react';
interface AddInventoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (inventoryData: any) => void;
}
export function AddInventoryModal({
  isOpen,
  onClose,
  onSave
}: AddInventoryModalProps) {
  const {
    t
  } = useTranslation('shops');
  const [roomType, setRoomType] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [availableRooms, setAvailableRooms] = useState('');
  const [closedToArrivalDates, setClosedToArrivalDates] = useState<string[]>([]);
  const [closedToDepartureDates, setClosedToDepartureDates] = useState<string[]>([]);
  const [blackoutDates, setBlackoutDates] = useState<string[]>([]);
  const [newClosedToArrivalDate, setNewClosedToArrivalDate] = useState('');
  const [newClosedToDepartureDate, setNewClosedToDepartureDate] = useState('');
  const [newBlackoutDate, setNewBlackoutDate] = useState('');
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const handleAddClosedToArrivalDate = () => {
    if (newClosedToArrivalDate && !closedToArrivalDates.includes(newClosedToArrivalDate)) {
      setClosedToArrivalDates([...closedToArrivalDates, newClosedToArrivalDate]);
      setNewClosedToArrivalDate('');
    }
  };
  const handleAddClosedToDepartureDate = () => {
    if (newClosedToDepartureDate && !closedToDepartureDates.includes(newClosedToDepartureDate)) {
      setClosedToDepartureDates([...closedToDepartureDates, newClosedToDepartureDate]);
      setNewClosedToDepartureDate('');
    }
  };
  const handleAddBlackoutDate = () => {
    if (newBlackoutDate && !blackoutDates.includes(newBlackoutDate)) {
      setBlackoutDates([...blackoutDates, newBlackoutDate]);
      setNewBlackoutDate('');
    }
  };
  const handleRemoveClosedToArrivalDate = (date: string) => {
    setClosedToArrivalDates(closedToArrivalDates.filter(d => d !== date));
  };
  const handleRemoveClosedToDepartureDate = (date: string) => {
    setClosedToDepartureDates(closedToDepartureDates.filter(d => d !== date));
  };
  const handleRemoveBlackoutDate = (date: string) => {
    setBlackoutDates(blackoutDates.filter(d => d !== date));
  };
  const handleSubmit = () => {
    const inventoryData = {
      roomType,
      fromDate,
      toDate,
      availableRooms: parseInt(availableRooms, 10) || 0,
      closedToArrivalDates,
      closedToDepartureDates,
      blackoutDates
    };
    onSave(inventoryData);
    onClose();
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="add-inventory-title" data-id="element-11330" data-mp="21vefc9b1uflq">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-11331" data-mp="25a7k04lohcx5">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-11332" data-mp="2ab3r0r1btf6s">
          <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-11333" data-mp="1vffp58jhd462"></div>
        </div>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-11334" data-mp="20e8rc4tbh8qx">
          <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4" data-id="element-11335" data-mp="2t0aawhduwcu6">
            <h3 id="add-inventory-title" className="text-lg font-medium text-gray-900" data-id="element-11336" data-mp="2ebe4eorg77vo">
              {t('components.AddInventoryModal.add_inventory')}
            </h3>
            <button onClick={onClose} aria-label={t('components.AddInventoryModal.add_inventory')} className="text-gray-400 hover:text-gray-500" data-id="element-11337" data-mp="36z9xzklq0jo8">
              <XIcon size={20} aria-hidden="true" data-id="element-11338" data-mp="1o4b0iwzk79ms" />
            </button>
          </div>
          <div className="p-6" data-id="element-11339" data-mp="1j2bwdkvzw8os">
            <p className="text-sm text-gray-500 mb-4" data-id="element-11340" data-mp="1j69ec36u6u73">
              {t('components.AddInventoryModal.select_the_room_type_and_applicable')}
            </p>
            <div className="space-y-4" data-id="element-11341" data-mp="197tmfxzzjgwj">
              {/* Room Type */}
              <div data-id="element-11342" data-mp="26sy3n9y6doxq">
                <label htmlFor="ai-room-type" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11343" data-mp="26vk830prrobq">
                  {t('components.AddInventoryModal.room_type')}
                </label>
                <div className="relative" data-id="element-11344" data-mp="1s5d0xh57prqc">
                  <select id="ai-room-type" className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 appearance-none focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={roomType} onChange={e => setRoomType(e.target.value)} data-id="element-11345" data-mp="1s4bfciqklmud">
                    <option value="" data-id="element-11346" data-mp="2x4jcni5yaat8">
                      {t('components.AddInventoryModal.select_room_type')}
                    </option>
                    <option value="premium-overwater" data-id="element-11347" data-mp="324nx1j9zbpoq">
                      {t('components.AddInventoryModal.premium_overwater_villa')}
                    </option>
                    <option value="water-villa" data-id="element-11348" data-mp="3g2cgqnsb8qs7">
                      {t('components.AddInventoryModal.water_villa')}
                    </option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-11349" data-mp="3ktug57eoqjb2">
                    <ChevronDownIcon size={16} className="text-gray-400" data-id="element-11350" data-mp="3pu9sbvn8dkrg" />
                  </div>
                </div>
              </div>
              {/* From Date */}
              <div data-id="element-11351" data-mp="3lqrdgt0rcfe">
                <label htmlFor="ai-from-date" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11352" data-mp="3l75xq45u5wm">
                  {t('components.AddInventoryModal.from')}
                </label>
                <input id="ai-from-date" type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={fromDate} onChange={e => setFromDate(e.target.value)} data-id="element-11353" data-mp="3jrr0xiiyeczp" />
              </div>
              {/* To Date */}
              <div data-id="element-11354" data-mp="1mnpzh6b36rd">
                <label htmlFor="ai-to-date" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11355" data-mp="1m4k4ytxk8tp">
                  {t('components.AddInventoryModal.to')}
                </label>
                <input id="ai-to-date" type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={toDate} onChange={e => setToDate(e.target.value)} data-id="element-11356" data-mp="23fln3p748li4" />
              </div>
              {/* Available Rooms */}
              <div data-id="element-11357" data-mp="3nscuir5u7s">
                <label htmlFor="ai-available-rooms" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11358" data-mp="7lbvh98sns7">
                  {t('components.AddInventoryModal.daily_available_rooms')}
                </label>
                <input id="ai-available-rooms" type="number" placeholder={t('components.AddInventoryModal.attrs.enter_daily_available_rooms')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={availableRooms} onChange={e => setAvailableRooms(e.target.value)} data-id="element-11359" data-mp="3rcbcx8965dea" />
              </div>
              {/* Closed to Arrival dates */}
              <div data-id="element-11360" data-mp="3uq8l5whhan6f">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11361" data-mp="3uqs0d5fquxgr">
                  {t('components.AddInventoryModal.closed_to_arrival_dates_in_this')}
                </label>
                <div className="space-y-2" data-id="element-11362" data-mp="3grim4bb3mdff">
                  {closedToArrivalDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-11363" data-mp="3bsp6odc94ccm" data-mpi={index}>
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-11364" data-mp="3buig20e91aob" />
                      <button onClick={() => handleRemoveClosedToArrivalDate(date)} className="ml-2 text-red-500" aria-label={`Remove ${date}`} data-id="element-11365" data-mp="1hjsghex3v43u">
                        <TrashIcon size={16} data-id="element-11366" data-mp="1rcr5uz0feyoi" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-11367" data-mp="ym935z3m6zbm">
                    <input type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newClosedToArrivalDate} onChange={e => setNewClosedToArrivalDate(e.target.value)} data-id="element-11368" data-mp="ynk5rvlbq115" />
                    <button onClick={handleAddClosedToArrivalDate} className="ml-2 text-red-500" data-id="element-11369" data-mp="3g2t7et3eb1mn">
                      <TrashIcon size={16} data-id="element-11370" data-mp="3pruokmlofp27" />
                    </button>
                  </div>
                  <button onClick={handleAddClosedToArrivalDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-11371" data-mp="3uqxvkcl7w5ct">
                    <PlusIcon size={16} className="mr-1" data-id="element-11372" data-mp="2x5jpxhhmry81" />
                    {t('components.AddInventoryModal.add')}
                  </button>
                </div>
              </div>
              {/* Closed to Departure dates */}
              <div data-id="element-11373" data-mp="3c0zdr4rmaxvz">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11374" data-mp="3c1hf2ft502ar">
                  {t('components.AddInventoryModal.closed_to_departure_dates_in_this')}
                </label>
                <div className="space-y-2" data-id="element-11375" data-mp="rcbmfvp49r1w">
                  {closedToDepartureDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-11376" data-mp="2bu2kjypo0clc" data-mpi={index}>
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-11377" data-mp="26ton1alowl1k" />
                      <button onClick={() => handleRemoveClosedToDepartureDate(date)} className="ml-2 text-red-500" aria-label={`Remove ${date}`} data-id="element-11378" data-mp="1s4iolouoeyuq">
                        <TrashIcon size={16} data-id="element-11379" data-mp="1j5onpmabbmbd" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-11380" data-mp="3dhlh3ksbf292">
                    <input type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newClosedToDepartureDate} onChange={e => setNewClosedToDepartureDate(e.target.value)} data-id="element-11381" data-mp="3dh3dg33bplr4" />
                    <button onClick={handleRemoveClosedToDepartureDate} className="ml-2 text-red-500" data-id="element-11382" data-mp="1pjb2ytujjnnh">
                      <TrashIcon size={16} data-id="element-11383" data-mp="1ugkxup0p8rip" />
                    </button>
                  </div>
                  <button onClick={handleAddClosedToDepartureDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-11384" data-mp="1fo0w9st76s3d">
                    <PlusIcon size={16} className="mr-1" data-id="element-11385" data-mp="48m2sz076id0" />
                    {t('components.AddInventoryModal.add')}
                  </button>
                </div>
              </div>
              {/* Blackout dates */}
              <div data-id="element-11386" data-mp="2ie92716yqnzz">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-11387" data-mp="2if22pkfbwh9o">
                  {t('components.AddInventoryModal.blackout_dates_in_this_period')}
                </label>
                <div className="space-y-2" data-id="element-11388" data-mp="2scyd2a0fz86q">
                  {blackoutDates.map((date, index) => <div key={index} className="flex items-center" data-id="element-11389" data-mp="24ndptex99orn" data-mpi={index}>
                      <input type="text" value={date} readOnly className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50" data-id="element-11390" data-mp="1zokffvd9mdvp" />
                      <button onClick={() => handleRemoveBlackoutDate(date)} className="ml-2 text-red-500" aria-label={`Remove ${date}`} data-id="element-11391" data-mp="1kymvxsqa6ccu">
                        <TrashIcon size={16} data-id="element-11392" data-mp="1tnk1llrc95ib" />
                      </button>
                    </div>)}
                  <div className="flex items-center" data-id="element-11393" data-mp="1ym57a0pd0he5">
                    <input type="text" placeholder={t('components.AddInventoryModal.attrs.mmddyyyy')} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" value={newBlackoutDate} onChange={e => setNewBlackoutDate(e.target.value)} data-id="element-11394" data-mp="23k6kw640noi8" />
                    <button onClick={handleRemoveBlackoutDate} className="ml-2 text-red-500" data-id="element-11395" data-mp="2rassxxe0t8s8">
                      <TrashIcon size={16} data-id="element-11396" data-mp="rz9nvtvu8knu" />
                    </button>
                  </div>
                  <button onClick={handleAddBlackoutDate} className="flex items-center text-red-600 text-sm font-medium" data-id="element-11397" data-mp="wycg2dqpywee">
                    <PlusIcon size={16} className="mr-1" data-id="element-11398" data-mp="kkfthhsntpq2" />
                    {t('components.AddInventoryModal.add')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-6 py-4 flex justify-end" data-id="element-11399" data-mp="alq8vh9dpr64">
            <button onClick={handleSubmit} className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-11400" data-mp="18aa3lrllh06m">
              {t('components.AddInventoryModal.create')}
            </button>
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-11401" data-mp="3e41omszujtq9">
      <polyline points="6 9 12 15 18 9" data-id="element-11402" data-mp="1w6wyu4uekk8t"></polyline>
    </svg>;
}